// src/admin/pages/CreateProductPage.tsx
import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/Layout/AdminLayout';
import { 
  FaImage, FaBold, FaItalic, FaUnderline, FaLink, 
  FaListUl, FaListOl, FaQuoteRight, FaCode, FaMinus, FaPlus 
} from 'react-icons/fa';
import Notification from '../components/Notification/Notification';

interface Category {
  id: number;
  name: string;
  icon: string;
  parentId: number | null;
  subCategories?: Category[];
}

interface CategoryType {
  id: number;
  name: string;
}

const CreateProduct: React.FC = () => {
  const [productName, setProductName] = useState<string>('');
  const [productCode, setProductCode] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [price, setPrice] = useState<string>('0.00');
  const [discount, setDiscount] = useState<string>('0');
  const [bulkDiscountPrice, setBulkDiscountPrice] = useState<string>('0.00');
  const [taxRate, setTaxRate] = useState<string>('0');
  const [offer, setOffer] = useState<string>('');
  const [finalPrice, setFinalPrice] = useState<string>('0.00');

  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedCategoryType, setSelectedCategoryType] = useState<string>('');
  const [stockQuantity, setStockQuantity] = useState<string>('0');
  const [measurementUnit, setMeasurementUnit] = useState<string>('');
  const [typeQuantity, setTypeQuantity] = useState<string>('0.00');
  const [productImages, setProductImages] = useState<File[]>([]);
  const [uploadedImageUrls, setUploadedImageUrls] = useState<string[]>([]);

  const [selectedCategoryName, setSelectedCategoryName] = useState<string>('Select Category...');
  const [isCategoryTreeOpen, setIsCategoryTreeOpen] = useState<boolean>(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());
  const [hierarchicalCategories, setHierarchicalCategories] = useState<Category[]>([]);

  const [categories, setCategories] = useState<Category[]>([]);
  const [categoryTypes, setCategoryTypes] = useState<CategoryType[]>([]);
  const [availableMeasurementUnits, setAvailableMeasurementUnits] = useState<string[]>([]);
  
  const [loadingCategories, setLoadingCategories] = useState<boolean>(true);
  const [loadingCategoryTypes, setLoadingCategoryTypes] = useState<boolean>(true);

  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' | 'info'; } | null>(null);

  const showNotification = (message: string, type: 'success' | 'error' | 'info') => {
    setNotification({ message, type });
  };

  const buildCategoryTree = (cats: Category[]) => {
    const categoriesMap = new Map<number, Category>();
    cats.forEach(cat => {
      categoriesMap.set(cat.id, { ...cat, subCategories: [] });
    });

    const rootCategories: Category[] = [];
    cats.forEach(cat => {
      if (cat.parentId === null || cat.parentId === undefined) {
        rootCategories.push(categoriesMap.get(cat.id)!);
      } else {
        const parent = categoriesMap.get(cat.parentId);
        if (parent) {
          parent.subCategories?.push(categoriesMap.get(cat.id)!);
        } else {
          rootCategories.push(categoriesMap.get(cat.id)!);
        }
      }
    });
    return rootCategories;
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch('https://ayushvatika.com/api/categories');
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data: Category[] = await response.json();
        setCategories(data);
        setHierarchicalCategories(buildCategoryTree(data));
      } catch (err: any) {
        showNotification(`Failed to fetch categories: ${err.message}`, 'error');
        console.error("Failed to fetch categories:", err);
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchCategoryTypes = async () => {
      try {
        const response = await fetch('https://ayushvatika.com/api/category-types');
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data: CategoryType[] = await response.json();
        setCategoryTypes(data);
      } catch (err: any) {
        showNotification(`Failed to fetch category types: ${err.message}`, 'error');
        console.error("Failed to fetch category types:", err);
      } finally {
        setLoadingCategoryTypes(false);
      }
    };
    fetchCategoryTypes();
  }, []);

  useEffect(() => {
    if (!selectedCategoryType) {
      setAvailableMeasurementUnits([]);
      setMeasurementUnit('');
      return;
    }

    const selectedType = categoryTypes.find(type => type.id === parseInt(selectedCategoryType));

    if (selectedType) {
      const typeName = selectedType.name.toLowerCase().trim();
      let units: string[] = [];

      if (typeName === 'liquid') {
        units = ['ml', 'litre'];
      } 
      else if (typeName === 'capsule' || typeName === 'tablets' || typeName === 'tablet') {
        units = ['pieces', 'strips'];
      } 
      else if (typeName === 'powder') {
        units = ['gm', 'kg'];
      } 
      else {
        units = ['pieces', 'kg', 'gm', 'ml', 'litre']; 
      }

      setAvailableMeasurementUnits(units);
      if (units.length > 0) {
        setMeasurementUnit(units[0]);
      } else {
        setMeasurementUnit('');
      }
    }
  }, [selectedCategoryType, categoryTypes]);

  useEffect(() => {
    const calculateFinalPrice = () => {
      const basePrice = parseFloat(price) || 0;
      const discountPercentage = parseFloat(discount) || 0;
      const taxPercentage = parseFloat(taxRate) || 0;

      let priceAfterDiscount = basePrice - (basePrice * discountPercentage / 100);
      if (priceAfterDiscount < 0) priceAfterDiscount = 0;

      const finalAmount = priceAfterDiscount + (priceAfterDiscount * taxPercentage / 100);

      setFinalPrice(finalAmount.toFixed(2));
    };

    calculateFinalPrice();
  }, [price, discount, taxRate]);

  const handleNumericChange = (e: React.ChangeEvent<HTMLInputElement>, setter: React.Dispatch<React.SetStateAction<string>>) => {
    const value = e.target.value;
    if (/^\d*\.?\d*$/.test(value)) {
      setter(value);
    }
  };

  const handleIntegerChange = (e: React.ChangeEvent<HTMLInputElement>, setter: React.Dispatch<React.SetStateAction<string>>) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setter(value);
    }
  };

  const handleImageDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files);
    const validFiles = files.filter(file => file.size <= 500 * 1024 && productImages.length + files.length <= 5);
    if (files.length !== validFiles.length) {
      showNotification('Some images exceeded the 500KB size limit or the 5 image limit.', 'info');
    }
    setProductImages(prev => [...prev, ...validFiles]);
  };

  const handleImageBrowse = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(file => file.size <= 500 * 1024 && productImages.length + files.length <= 5);
    if (files.length !== validFiles.length) {
      showNotification('Some images exceeded the 500KB size limit or the 5 image limit.', 'info');
    }
    setProductImages(prev => [...prev, ...validFiles]);
  };

  const removeImage = (index: number) => {
    setProductImages(prev => prev.filter((_, i) => i !== index));
    setUploadedImageUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handleCreateProduct = async () => {
    if (!productName || !selectedCategory || !selectedCategoryType || !measurementUnit || parseFloat(price) <= 0 || parseInt(stockQuantity) < 0 || parseFloat(typeQuantity) <= 0) {
      showNotification('Please fill in all required fields and ensure prices/quantities are valid.', 'error');
      return;
    }

    let imageUrlToSave: string | null = null;
    const uploadedUrls: string[] = [];

    if (productImages.length > 0) {
      try {
        for (const file of productImages) {
          const formData = new FormData();
          formData.append('file', file);

          const uploadResponse = await fetch('https://ayushvatika.com/api/products/upload-image', {
            method: 'POST',
            body: formData,
          });

          if (!uploadResponse.ok) {
            const errorText = await uploadResponse.text();
            throw new Error(`Failed to upload image: ${uploadResponse.status} ${uploadResponse.statusText} - ${errorText}`);
          }
          const uploadedFileName = await uploadResponse.text();
          uploadedUrls.push(uploadedFileName);
        }
        if (uploadedUrls.length > 0) {
          imageUrlToSave = uploadedUrls[0];
        }
      } catch (err: any) {
        console.error("Error uploading images:", err);
        showNotification(`Error uploading images: ${err.message}`, 'error');
        return;
      }
    }

    const productData = {
      productName,
      productCode,
      description,
      price: parseFloat(price),
      bulkDiscountPrice: parseFloat(bulkDiscountPrice),
      discount: parseFloat(discount),
      taxRate: parseFloat(taxRate),
      offer: offer,
      category: { id: parseInt(selectedCategory) },
      categoryType: { id: parseInt(selectedCategoryType) },
      stockQuantity: parseInt(stockQuantity),
      typeQuantity: parseFloat(typeQuantity),
      measurementUnit,
      imageUrl: imageUrlToSave,
    };

    try {
      const response = await fetch('https://ayushvatika.com/api/products', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(productData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create product: ${response.status} ${response.statusText} - ${errorText}`);
      }

      showNotification('Product created successfully!', 'success');
      handleDiscard();
    } catch (err: any) {
      console.error("Error creating product:", err);
      showNotification(`Error creating product: ${err.message}`, 'error');
    }
  };

  const handleDiscard = () => {
    setProductName('');
    setProductCode('');
    setDescription('');
    setPrice('0.00');
    setBulkDiscountPrice('0.00');
    setTaxRate('0');
    setOffer('');
    setDiscount('0');
    setFinalPrice('0.00');

    setSelectedCategory('');
    setSelectedCategoryType('');
    setStockQuantity('0');
    setMeasurementUnit('');
    setTypeQuantity('0.00');
    setProductImages([]);
    setUploadedImageUrls([]);
    setAvailableMeasurementUnits([]);
    setSelectedCategoryName('Select Category...');
  };

  // --- STYLES FOR DYNAMIC THEME (LIGHT & DARK) ---
  const inputBaseStyle = "w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 transition-all shadow-sm bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400";
  const labelBaseStyle = "block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1 uppercase tracking-wide";

  function renderCategoryTree(categories: Category[], level: number = 0): React.ReactNode {
    const renderNode = (category: Category, level: number = 0) => {
      const hasChildren = category.subCategories && category.subCategories.length > 0;
      const isExpanded = expandedCategories.has(category.id);

      return (
        <div key={category.id} style={{ paddingLeft: `${level * 16}px` }}>
          <div
            className={`flex items-center cursor-pointer px-2 py-2 rounded transition-colors ${
              selectedCategory === String(category.id) 
              ? 'bg-green-100 text-green-700 font-bold dark:bg-green-900/30 dark:text-green-400' 
              : 'text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-600'
            }`}
            onClick={() => {
              setSelectedCategory(String(category.id));
              setSelectedCategoryName(category.name);
              setIsCategoryTreeOpen(false);
            }}
          >
            {hasChildren && (
              <button
                type="button"
                className="mr-2 text-xs text-gray-500 dark:text-gray-400 focus:outline-none"
                onClick={e => {
                  e.stopPropagation();
                  setExpandedCategories(prev => {
                    const next = new Set(prev);
                    if (next.has(category.id)) {
                      next.delete(category.id);
                    } else {
                      next.add(category.id);
                    }
                    return next;
                  });
                }}
              >
                {isExpanded ? '▼' : '▶'}
              </button>
            )}
            <span className="flex items-center gap-2">
              {category.icon && (
                <span
                  className="inline-block w-4 h-4 fill-current"
                  dangerouslySetInnerHTML={{ __html: category.icon }}
                />
              )}
              {category.name}
            </span>
          </div>
          {hasChildren && isExpanded && (
            <div className="border-l border-gray-300 dark:border-gray-600 ml-2">
              {category.subCategories!.map(child => renderNode(child, level + 1))}
            </div>
          )}
        </div>
      );
    };

    return categories.map(cat => renderNode(cat));
  }

  return (
    <AdminLayout>
      {/* Background: Dynamic Light/Dark */}
      <div className="min-h-screen p-6 md:p-8 font-sans transition-colors bg-gray-50 dark:bg-gray-900"> 
        <h1 className="text-3xl font-bold mb-8 flex items-center text-gray-800 dark:text-white">
            <FaPlus className="mr-3 text-green-600 dark:text-green-500" /> Create Product
        </h1>

        {notification && (
          <Notification
            message={notification.message}
            type={notification.type}
            onClose={() => setNotification(null)}
          />
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Basic Information Card */}
            <div className="p-6 rounded-xl shadow-lg border transition-colors bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-700">
              <h2 className="text-xl font-bold mb-6 pb-2 border-b text-gray-900 dark:text-white border-gray-100 dark:border-gray-700">Basic Information</h2>
              
              <div className="mb-5">
                <label htmlFor="productName" className={labelBaseStyle}>Product name</label>
                <input
                  type="text"
                  id="productName"
                  placeholder="e.g. Alovera Juice"
                  className={inputBaseStyle}
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                />
              </div>
              
              <div className="mb-5">
                <label htmlFor="productCode" className={labelBaseStyle}>Product code</label>
                <input
                  type="text"
                  id="productCode"
                  placeholder="e.g. ALJC001"
                  className={inputBaseStyle}
                  value={productCode}
                  onChange={(e) => setProductCode(e.target.value)}
                />
              </div>
              
              <div>
                <label htmlFor="description" className={labelBaseStyle}>Description</label>
                <div className="border rounded-lg overflow-hidden shadow-sm bg-gray-50 border-gray-300 dark:bg-gray-700 dark:border-gray-600">
                  {/* Toolbar */}
                  <div className="flex p-2 space-x-1 border-b bg-gray-100 border-gray-300 dark:bg-gray-600 dark:border-gray-500">
                    <button className="p-2 rounded-md transition-colors text-gray-600 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-500"><FaBold /></button>
                    <button className="p-2 rounded-md transition-colors text-gray-600 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-500"><FaItalic /></button>
                    <button className="p-2 rounded-md transition-colors text-gray-600 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-500"><FaUnderline /></button>
                    <div className="w-px bg-gray-400 mx-2 h-6 self-center"></div>
                    <button className="p-2 rounded-md transition-colors text-gray-600 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-500"><FaLink /></button>
                    <button className="p-2 rounded-md transition-colors text-gray-600 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-500"><FaListUl /></button>
                    <button className="p-2 rounded-md transition-colors text-gray-600 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-500"><FaListOl /></button>
                  </div>
                  <textarea
                    id="description"
                    placeholder="Product Description..."
                    rows={6}
                    className="w-full p-4 outline-none resize-y bg-white text-gray-900 placeholder-gray-400 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  ></textarea>
                </div>
              </div>
            </div>

            {/* Pricing Card */}
            <div className="p-6 rounded-xl shadow-lg border transition-colors bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-700">
              <h2 className="text-xl font-bold mb-6 pb-2 border-b text-gray-900 dark:text-white border-gray-100 dark:border-gray-700">Pricing</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="price" className={labelBaseStyle}>Price</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">₹</span>
                    <input
                      type="text"
                      id="price"
                      className={`${inputBaseStyle} pl-8`}
                      value={price}
                      onChange={(e) => handleNumericChange(e, setPrice)}
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="discount" className={labelBaseStyle}>Discount (%)</label>
                  <input
                    type="text"
                    id="discount"
                    placeholder="e.g., 10"
                    className={inputBaseStyle}
                    value={discount}
                    onChange={(e) => handleNumericChange(e, setDiscount)}
                  />
                </div>
                <div>
                  <label htmlFor="bulkPrice" className={labelBaseStyle}>Bulk Price (₹)</label>
                  <input
                    type="text"
                    id="bulkPrice"
                    className={inputBaseStyle}
                    value={bulkDiscountPrice}
                    onChange={(e) => handleNumericChange(e, setBulkDiscountPrice)}
                  />
                </div>
                <div>
                  <label htmlFor="taxRate" className={labelBaseStyle}>Tax rate (%)</label>
                  <input
                    type="text"
                    id="taxRate"
                    className={inputBaseStyle}
                    value={taxRate}
                    onChange={(e) => handleNumericChange(e, setTaxRate)}
                  />
                </div>
                <div>
                  <label htmlFor="offer" className={labelBaseStyle}>Offer</label>
                  <input
                    type="text"
                    id="offer"
                    placeholder="e.g., Buy 1 Get 1 Free"
                    className={inputBaseStyle}
                    value={offer}
                    onChange={(e) => setOffer(e.target.value)}
                  />
                </div>
                <div className="md:col-span-1">
                  <label htmlFor="finalPrice" className={labelBaseStyle}>Final Price</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">₹</span>
                    <input
                      type="text"
                      id="finalPrice"
                      className="w-full p-3 pl-8 border rounded-lg cursor-not-allowed font-medium bg-gray-100 border-gray-300 text-green-600 dark:bg-gray-900 dark:border-gray-600 dark:text-green-400"
                      value={finalPrice}
                      readOnly
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="lg:col-span-1 space-y-6">
            
            {/* Product Image Card */}
            <div className="p-6 rounded-xl shadow-lg border transition-colors bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-700">
              <h2 className="text-xl font-bold mb-6 pb-2 border-b text-gray-900 dark:text-white border-gray-100 dark:border-gray-700">Product Image</h2>
              <p className="text-sm mb-4 text-gray-500 dark:text-gray-400">Choose a product photo or simply drag and drop up to 5 photos here.</p>
              <div
                className="border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all bg-gray-50 border-gray-300 hover:border-green-500 dark:bg-gray-700/50 dark:border-gray-600 dark:hover:border-green-500"
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleImageDrop}
                onClick={() => document.getElementById('imageUploadInput')?.click()}
              >
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-gray-200 dark:bg-gray-600">
                  <FaImage className="text-2xl text-gray-500 dark:text-gray-300" />
                </div>
                <p className="mb-1 text-gray-600 dark:text-gray-300">Drop your image here, or</p>
                <p className="text-green-600 font-bold hover:underline dark:text-green-500">Click to browse</p>
                <input
                  type="file"
                  id="imageUploadInput"
                  multiple
                  accept="image/jpeg,image/jpg,image/png,image/gif"
                  className="hidden"
                  onChange={handleImageBrowse}
                />
              </div>
              
              {productImages.length > 0 && (
                <div className="mt-6 grid grid-cols-3 gap-3">
                  {productImages.map((file, index) => (
                    <div key={index} className="relative group rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`Product Image ${index + 1}`}
                        className="w-full h-20 object-cover"
                      />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <button
                          onClick={() => removeImage(index)}
                          className="bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600"
                          aria-label="Remove image"
                        >
                          &times;
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs mt-4 text-gray-500 dark:text-gray-500">
                Image formats: .jpg, .jpeg, .png. Max size: 500kb.
              </p>
            </div>

            {/* Organization Card */}
            <div className="p-6 rounded-xl shadow-lg border transition-colors bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-700">
              <h2 className="text-xl font-bold mb-6 pb-2 border-b text-gray-900 dark:text-white border-gray-100 dark:border-gray-700">Organization</h2>
              
              <div className="mb-5">
                <label htmlFor="category" className={labelBaseStyle}>Category</label>
                <div className="relative">
                  <div
                    className={`${inputBaseStyle} cursor-pointer flex justify-between items-center`}
                    onClick={() => setIsCategoryTreeOpen(!isCategoryTreeOpen)}
                  >
                    <span>{selectedCategoryName}</span>
                    <span className="text-xs text-gray-400">▼</span>
                  </div>
                  {isCategoryTreeOpen && (
                    <div className="absolute z-10 w-full mt-1 rounded-lg shadow-2xl max-h-60 overflow-y-auto bg-white border border-gray-200 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200">
                      {loadingCategories ? (
                        <p className="p-3 text-sm text-gray-500 dark:text-gray-400">Loading categories...</p>
                      ) : (
                        <div className="py-1">
                           {renderCategoryTree(hierarchicalCategories)}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-5">
                <label htmlFor="categoryType" className={labelBaseStyle}>Category Type</label>
                <select
                  id="categoryType"
                  className={inputBaseStyle}
                  value={selectedCategoryType}
                  onChange={(e) => setSelectedCategoryType(e.target.value)}
                  disabled={loadingCategoryTypes}
                >
                  <option value="">{loadingCategoryTypes ? 'Loading...' : 'Select Category Type...'}</option>
                  {categoryTypes.map((type) => (
                    <option key={type.id} value={type.id} className="bg-white text-gray-900 dark:bg-gray-700 dark:text-white">{type.name}</option>
                  ))}
                </select>
              </div>

              <div className="mb-5">
                <label htmlFor="typeQuantity" className={labelBaseStyle}>Unit of Measurement</label>
                <div className="flex">
                  <input
                    type="text"
                    id="typeQuantity"
                    placeholder="e.g., 500"
                    className={`${inputBaseStyle} rounded-r-none border-r-0`}
                    style={{width: '65%'}}
                    value={typeQuantity}
                    onChange={(e) => handleNumericChange(e, setTypeQuantity)}
                  />
                  <select
                    id="measurementUnit"
                    className={`${inputBaseStyle} rounded-l-none border-l bg-gray-100 border-l-gray-300 dark:bg-gray-600 dark:border-l-gray-600`}
                    style={{width: '35%'}}
                    value={measurementUnit}
                    onChange={(e) => setMeasurementUnit(e.target.value)}
                    disabled={!selectedCategoryType}
                  >
                    <option value="" className="bg-white text-gray-900 dark:bg-gray-700 dark:text-white">Unit</option>
                    {availableMeasurementUnits.map((unit) => (
                      <option key={unit} value={unit} className="bg-white text-gray-900 dark:bg-gray-700 dark:text-white">{unit}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mb-2">
                <label htmlFor="stockQuantity" className={labelBaseStyle}>Stock Quantity</label>
                <input
                  type="text"
                  id="stockQuantity"
                  placeholder="e.g., 100"
                  className={inputBaseStyle}
                  value={stockQuantity}
                  onChange={(e) => handleIntegerChange(e, setStockQuantity)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4 mt-8 pt-4 border-t border-gray-300 dark:border-gray-700">
          <button
            onClick={handleDiscard}
            className="px-8 py-3 rounded-lg border font-bold shadow-sm transition-colors border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Discard
          </button>
          <button
            onClick={handleCreateProduct}
            className="px-8 py-3 rounded-lg bg-green-600 text-white hover:bg-green-700 hover:shadow-lg transition-all font-bold shadow-md"
          >
            Create Product
          </button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default CreateProduct;